/**
 * Country name mapping utility to handle discrepancies between
 * mock data country names and geography data country names
 */

// Mapping from mock data country names to standardized names used in geography data
export const countryNameMapping = {
  // Standard mappings (mock data name -> geography data name)
  'United States': 'United States of America',
  Russia: 'Russian Federation',
  'South Korea': 'Republic of Korea',
  'North Korea': "Democratic People's Republic of Korea",
  'Myanmar [Burma]': 'Myanmar',
  'Czech Republic': 'Czechia',
  'Republic of Korea': 'South Korea',
  'Republic of Lithuania': 'Lithuania',
  'Republic of Moldova': 'Moldova',
  'Hashemite Kingdom of Jordan': 'Jordan',
  "Cote d'Ivoire": 'Ivory Coast',
  'U.S. Virgin Islands': 'United States Virgin Islands',
  'Virgin Islands': 'United States Virgin Islands',
  Reunion: 'Réunion',
  'Sao Tome and Principe': 'São Tomé and Príncipe',
  Macedonia: 'North Macedonia',
  Congo: 'Republic of the Congo',
  Aland: 'Åland Islands',
  Macao: 'Macau',

  // Additional mappings that might appear in data
  UK: 'United Kingdom',
  USA: 'United States of America',
  UAE: 'United Arab Emirates',
  DRC: 'Democratic Republic of the Congo',
  'DR Congo': 'Democratic Republic of the Congo'
};

// Reverse mapping (geography data name -> mock data name)
export const reverseCountryNameMapping = Object.fromEntries(
  Object.entries(countryNameMapping).map(([key, value]) => [value, key])
);

/**
 * Get standardized country name for geography matching
 * @param {string} mockDataName - Country name from mock data
 * @returns {string} - Standardized name for geography matching
 */
export const getStandardizedCountryName = mockDataName => countryNameMapping[mockDataName] || mockDataName;

/**
 * Enhanced country matching function that handles name variations
 * @param {Object} countryData - Country data object with id, name properties
 * @param {Object} geoProperties - Geography properties object
 * @returns {boolean} - Whether the country matches
 */
export const isCountryMatch = (countryData, geoProperties) => {
  if (!countryData || !geoProperties) return false;

  const { id: countryCode, name: countryName } = countryData;

  // Direct code matches (highest priority)
  const codeMatches = [
    geoProperties.ISO_A2,
    geoProperties.iso_a2,
    geoProperties.ISO_A3,
    geoProperties.iso_a3,
    geoProperties.ADM0_A3,
    geoProperties.adm0_a3
  ];

  if (codeMatches.some(code => code === countryCode)) {
    return true;
  }

  // Name matching with standardization
  const geoNames = [
    geoProperties.NAME,
    geoProperties.name,
    geoProperties.NAME_EN,
    geoProperties.name_en,
    geoProperties.ADMIN,
    geoProperties.admin,
    geoProperties.NAME_LONG,
    geoProperties.name_long
  ].filter(Boolean);

  const standardizedCountryName = getStandardizedCountryName(countryName);

  // Check if any geography name matches our country name or its standardized version
  return geoNames.some(geoName => {
    const lowerGeoName = geoName.toLowerCase();
    const lowerCountryName = countryName.toLowerCase();
    const lowerStandardizedName = standardizedCountryName.toLowerCase();

    return (
      lowerGeoName === lowerCountryName ||
      lowerGeoName === lowerStandardizedName ||
      // Handle some common geography variations
      (lowerCountryName === 'united states' && lowerGeoName === 'united states of america') ||
      (lowerCountryName === 'russia' && lowerGeoName === 'russian federation') ||
      (lowerCountryName === 'south korea' && lowerGeoName === 'republic of korea') ||
      (lowerCountryName === 'north korea' && lowerGeoName === "democratic people's republic of korea") ||
      (lowerCountryName === 'czech republic' && lowerGeoName === 'czechia') ||
      (lowerCountryName === 'czechia' && lowerGeoName === 'czech republic')
    );
  });
};

/**
 * Find country data that matches a geography object
 * @param {Array} countries - Array of country data objects
 * @param {Object} geo - Geography object with properties
 * @returns {Object|null} - Matching country data or null
 */
export const findMatchingCountryData = (countries, geo) =>
  countries.find(country => isCountryMatch(country, geo.properties || {}));

/**
 * Check if a geography object matches the hovered country
 * @param {Object} hoveredCountry - Currently hovered country data
 * @param {Object} geo - Geography object with properties
 * @returns {boolean} - Whether this geography matches the hovered country
 */
export const isGeographyHovered = (hoveredCountry, geo) => {
  if (!hoveredCountry) return false;
  return isCountryMatch(hoveredCountry, geo.properties || {});
};
